/**
 * Alert Store
 *
 * Zustand store for managing alert rules and triggered alerts state.
 * Handles loading, CRUD operations, and unread alert tracking.
 *
 * @module store/alertStore
 */

import { create } from 'zustand';
import type { AlertRule, TriggeredAlert, CreateAlertRuleRequest, UpdateAlertRuleRequest } from '@budgira/shared';
import { alertService } from '../services/alert.service';

/**
 * Alert store state interface
 */
interface AlertState {
  // Alert rules
  rules: AlertRule[];
  rulesLoading: boolean;
  rulesError: string | null;

  // Triggered alerts
  alerts: TriggeredAlert[];
  unreadCount: number;
  alertsLoading: boolean;
  alertsError: string | null;

  // Last check timestamp
  lastChecked: Date | null;

  // Actions - Rules
  fetchRules: () => Promise<void>;
  createRule: (data: CreateAlertRuleRequest) => Promise<AlertRule | null>;
  updateRule: (id: string, data: UpdateAlertRuleRequest) => Promise<AlertRule | null>;
  deleteRule: (id: string) => Promise<boolean>;
  toggleRuleActive: (id: string, isActive: boolean) => Promise<boolean>;

  // Actions - Alerts
  checkAlerts: () => Promise<void>;
  markAsRead: (alertId: string) => Promise<boolean>;
  markAllAsRead: () => Promise<boolean>;
  fetchHistory: (limit?: number) => Promise<TriggeredAlert[]>;

  // Utility
  clearErrors: () => void;
  reset: () => void;
}

/**
 * Initial state
 */
const initialState = {
  rules: [] as AlertRule[],
  rulesLoading: false,
  rulesError: null as string | null,
  alerts: [] as TriggeredAlert[],
  unreadCount: 0,
  alertsLoading: false,
  alertsError: null as string | null,
  lastChecked: null as Date | null,
};

/**
 * Alert store
 */
export const useAlertStore = create<AlertState>((set) => ({
  ...initialState,

  // ============================================
  // Alert Rules Actions
  // ============================================

  fetchRules: async () => {
    set({ rulesLoading: true, rulesError: null });
    try {
      const response = await alertService.getRules();
      if (response.success && response.data) {
        set({ rules: response.data, rulesLoading: false });
      } else {
        set({
          rulesError: response.error?.message || 'Erreur lors du chargement des règles',
          rulesLoading: false,
        });
      }
    } catch (error) {
      console.error('Error fetching alert rules:', error);
      set({
        rulesError: 'Erreur de connexion',
        rulesLoading: false,
      });
    }
  },

  createRule: async (data: CreateAlertRuleRequest) => {
    set({ rulesLoading: true, rulesError: null });
    try {
      const response = await alertService.createRule(data);
      if (response.success && response.data) {
        set((state) => ({
          rules: [response.data!, ...state.rules],
          rulesLoading: false,
        }));
        return response.data;
      } else {
        set({
          rulesError: response.error?.message || 'Erreur lors de la création',
          rulesLoading: false,
        });
        return null;
      }
    } catch (error) {
      console.error('Error creating alert rule:', error);
      set({
        rulesError: 'Erreur de connexion',
        rulesLoading: false,
      });
      return null;
    }
  },

  updateRule: async (id: string, data: UpdateAlertRuleRequest) => {
    set({ rulesLoading: true, rulesError: null });
    try {
      const response = await alertService.updateRule(id, data);
      if (response.success && response.data) {
        set((state) => ({
          rules: state.rules.map((r) => (r.id === id ? response.data! : r)),
          rulesLoading: false,
        }));
        return response.data;
      } else {
        set({
          rulesError: response.error?.message || 'Erreur lors de la mise à jour',
          rulesLoading: false,
        });
        return null;
      }
    } catch (error) {
      console.error('Error updating alert rule:', error);
      set({
        rulesError: 'Erreur de connexion',
        rulesLoading: false,
      });
      return null;
    }
  },

  deleteRule: async (id: string) => {
    set({ rulesLoading: true, rulesError: null });
    try {
      const response = await alertService.deleteRule(id);
      if (response.success) {
        set((state) => ({
          rules: state.rules.filter((r) => r.id !== id),
          rulesLoading: false,
        }));
        return true;
      } else {
        set({
          rulesError: response.error?.message || 'Erreur lors de la suppression',
          rulesLoading: false,
        });
        return false;
      }
    } catch (error) {
      console.error('Error deleting alert rule:', error);
      set({
        rulesError: 'Erreur de connexion',
        rulesLoading: false,
      });
      return false;
    }
  },

  toggleRuleActive: async (id: string, isActive: boolean) => {
    try {
      const response = await alertService.toggleRuleActive(id, isActive);
      if (response.success && response.data) {
        set((state) => ({
          rules: state.rules.map((r) => (r.id === id ? response.data! : r)),
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error toggling alert rule:', error);
      return false;
    }
  },

  // ============================================
  // Triggered Alerts Actions
  // ============================================

  checkAlerts: async () => {
    set({ alertsLoading: true, alertsError: null });
    try {
      const response = await alertService.checkAlerts();
      if (response.success && response.data) {
        set({
          alerts: response.data.alerts,
          unreadCount: response.data.unreadCount,
          alertsLoading: false,
          lastChecked: new Date(),
        });
      } else {
        set({
          alertsError: response.error?.message || 'Erreur lors de la vérification',
          alertsLoading: false,
        });
      }
    } catch (error) {
      console.error('Error checking alerts:', error);
      set({
        alertsError: 'Erreur de connexion',
        alertsLoading: false,
      });
    }
  },

  markAsRead: async (alertId: string) => {
    try {
      const response = await alertService.markAsRead(alertId);
      if (response.success && response.data) {
        set((state) => ({
          alerts: state.alerts.map((a) => (a.id === alertId ? response.data! : a)),
          unreadCount: Math.max(0, state.unreadCount - 1),
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error marking alert as read:', error);
      return false;
    }
  },

  markAllAsRead: async () => {
    try {
      const response = await alertService.markAllAsRead();
      if (response.success) {
        set((state) => ({
          alerts: state.alerts.map((a) => ({ ...a, isRead: true, readAt: new Date().toISOString() })),
          unreadCount: 0,
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error marking all alerts as read:', error);
      return false;
    }
  },

  fetchHistory: async (limit?: number) => {
    try {
      const response = await alertService.getHistory(limit);
      if (response.success && response.data) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching alert history:', error);
      return [];
    }
  },

  // ============================================
  // Utility Actions
  // ============================================

  clearErrors: () => {
    set({ rulesError: null, alertsError: null });
  },

  reset: () => {
    set(initialState);
  },
}));

export default useAlertStore;
